import type { DashboardTab, TabGroup } from "@app/config/tabs";

/**
 * Map backend tab IDs (with underscores) to frontend tab IDs (with dashes).
 * 
 * Backend returns: "trading_system", "admin_users", "user_trading_system"
 * Frontend uses: "trading-system", "users", "user-trading-system"
 */
const TAB_ID_MAPPING: Record<string, string> = {
  dashboard: "overview",
  trading_system: "trading-system",
  connections: "connections",
  integrations: "integrations",
  market_data: "market-data",
  ai_assistant: "ai-assistant",
  ai_supervisor: "ai-supervisor",
  ai_engine_monitor: "ai-engine-monitor",
  trading_logic_controls: "trading-logic-controls",
  user_trading_system: "user-trading-system",
  admin_users: "users",
  admin_plans: "plans",
  admin_subscriptions: "subscriptions",
  admin_kill_switches: "admin-trading-pipeline",
  admin_settings: "system",
  admin_trading_pipeline: "admin-trading-pipeline",
  admin_coins_wallets: "admin-coins-wallets",
  admin_payment_monitoring: "admin-payment-monitoring",
  admin_support: "admin-support",
  admin_operations: "admin-operations",
  admin_dlq: "admin-dlq",
  admin_exit_signals: "admin-exit-signals",
  admin_reconciliation: "admin-reconciliation",
  admin_positions_monitor: "admin-positions-monitor",
  admin_health_ops: "admin-health-ops",
  admin_system_storage: "admin-system-storage",
  admin_trade_execution: "admin-trade-execution",
  admin_trade_history: "admin-trade-history",
  xgboost_control_center: "xgboost-control-center",
  lgbm_control_center: "lgbm-control-center",
  trade_flow_tracking: "trade-flow-tracking",
  system: "system",
};

/**
 * Filter dashboard tabs based on allowed_tabs from backend.
 * 
 * @param allTabs - All available tabs from config
 * @param allowedTabs - List of allowed tab IDs from backend (with underscores)
 * @returns Filtered list of tabs that user is allowed to see
 */
export function filterTabsByAllowed(
  allTabs: DashboardTab[],
  allowedTabs: string[] | undefined,
): DashboardTab[] {
  if (!allowedTabs || allowedTabs.length === 0) {
    return [];
  }

  const allowedFrontendIds = new Set<string>();

  for (const backendTabId of allowedTabs) {
    const frontendId = TAB_ID_MAPPING[backendTabId] || backendTabId;
    allowedFrontendIds.add(frontendId);
  }

  return allTabs.filter((tab) => allowedFrontendIds.has(tab.id));
}

/**
 * Filter tab groups to only show groups that have at least one allowed tab.
 * Maintains the original group order from tabGroups config.
 * 
 * @param allGroups - All defined tab groups
 * @param visibleTabs - Already filtered list of allowed tabs
 * @returns Filtered list of groups that contain at least one visible tab
 */
export function filterGroupsByVisibleTabs(
  allGroups: TabGroup[],
  visibleTabs: DashboardTab[],
): TabGroup[] {
  const visibleGroupIds = new Set(visibleTabs.map((t) => t.group));
  return allGroups.filter((g) => visibleGroupIds.has(g.id));
}

/**
 * Get the tabs that belong to a specific group from the visible tabs.
 * 
 * @param groupId - The group ID to filter by
 * @param visibleTabs - Already filtered list of allowed tabs
 * @returns List of tabs belonging to the group
 */
export function getTabsForGroup(
  groupId: string,
  visibleTabs: DashboardTab[],
): DashboardTab[] {
  return visibleTabs.filter((t) => t.group === groupId);
}
